// JQ4-29 — US-TICKETS-009: Supervisor real-time operations dashboard page.
//
// Design decisions implemented here:
//   D5  — elapsed computed client-side from server timestamps (pickElapsedAnchor + useElapsed)
//   D6  — two independent queries: refetchInterval 8s, keepPreviousData → no skeleton flash on bg poll
//   D7  — dedicated route, reads activeLocationId from localStorage (AppShell already manages it)
//   D8  — empty state ONLY when activeTickets=0 AND all shift metrics are zero
//
// REQ-02: pending tickets (requested, no valet) always at the top, visually highlighted.
// REQ-04: < 10s refresh SLA satisfied by 8s polling interval.
// REQ-06: true empty state vs. partial state (metrics visible even when no active tickets).

import {
  getMyLocations,
  getSupervisorActiveTickets,
  getSupervisorShiftMetrics,
} from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { SupervisorActiveTicketDTO, SupervisorShiftMetricsDTO } from '@jq4race/shared';
import { Badge, Card, CardContent, Skeleton } from '@jq4race/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Activity,
  Car,
  CheckCheck,
  Clock,
  type LucideIcon,
  RefreshCw,
  TriangleAlert,
  UserCheck,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';

import { pickElapsedAnchor, useElapsed } from '../../../../lib/supervisor';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import {
  setActiveLocationId,
  useActiveLocationId,
} from '../../../app-shell/lib/active-location-store';

// ── Main page ────────────────────────────────────────────────────────────────

export function SupervisorDashboardPage() {
  const { t } = useTranslation('supervisor-dashboard');
  // Reactive: AppShell auto-selects a single assigned location asynchronously,
  // so a one-shot localStorage read here left the page stuck until a reload.
  const locationId = useActiveLocationId();

  // ── Active-tickets query ─────────────────────────────────────────────────
  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    isFetching: ticketsFetching,
    isError: ticketsError,
    error: ticketsErrorObj,
  } = useQuery({
    queryKey: ['supervisor-active-tickets', locationId],
    queryFn: () => getSupervisorActiveTickets(locationId as string),
    enabled: !!locationId,
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

  // ── Shift-metrics query ──────────────────────────────────────────────────
  const {
    data: metricsData,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
    isError: metricsError,
    error: metricsErrorObj,
  } = useQuery({
    queryKey: ['supervisor-shift-metrics', locationId],
    queryFn: () => getSupervisorShiftMetrics(locationId as string),
    enabled: !!locationId,
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  });

  // ── Render guards ────────────────────────────────────────────────────────
  if (!locationId) {
    return <NoLocationState />;
  }

  const showTicketsSkeleton = ticketsLoading && !ticketsData;
  const showMetricsSkeleton = metricsLoading && !metricsData;
  const isUpdating = (ticketsFetching && !ticketsLoading) || (metricsFetching && !metricsLoading);

  const items = ticketsData?.items ?? [];
  const metrics = metricsData;

  // Design D8: true empty = no active tickets AND all metric counters are zero
  const metricsAllZero =
    !metrics ||
    (metrics.checkInsToday === 0 &&
      metrics.deliveriesToday === 0 &&
      metrics.avgRetrievalSeconds === 0 &&
      metrics.activeValets === 0 &&
      metrics.activeNow === 0 &&
      metrics.pendingRetrieval === 0);

  const showTrueEmpty =
    !showTicketsSkeleton && !showMetricsSkeleton && items.length === 0 && metricsAllZero;

  return (
    <div className="space-y-8">
      {/* ----- Page header ----- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="jq-eyebrow jq-eyebrow--leader mb-1">{t('header.eyebrow')}</p>
          <h2 className="font-editorial text-[32px] font-medium leading-tight tracking-tight text-jq-navy sm:text-[40px]">
            {t('header.title')}
          </h2>
        </div>

        {isUpdating && (
          <span className="flex items-center gap-1.5 text-[12px] text-jq-graphite">
            <RefreshCw className="h-3 w-3 animate-spin" strokeWidth={1.75} />
            {t('header.updating')}
          </span>
        )}
      </div>

      {/* ----- True empty state ----- */}
      {showTrueEmpty ? (
        <TrueEmptyState />
      ) : (
        <>
          {/* ----- Shift metrics strip ----- */}
          <section>
            <p className="jq-eyebrow mb-4">{t('metrics.sectionTitle')}</p>
            {metricsError ? (
              <ErrorState error={metricsErrorObj} />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <MetricCard
                  icon={Users}
                  label={t('metrics.checkIns')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : (metrics as SupervisorShiftMetricsDTO).checkInsToday.toString()
                  }
                />
                <MetricCard
                  icon={CheckCheck}
                  label={t('metrics.deliveries')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : (metrics as SupervisorShiftMetricsDTO).deliveriesToday.toString()
                  }
                />
                <MetricCard
                  icon={Car}
                  label={t('metrics.activeNow')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : (metrics as SupervisorShiftMetricsDTO).activeNow.toString()
                  }
                />
                <MetricCard
                  icon={Activity}
                  label={t('metrics.pendingRetrieval')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : (metrics as SupervisorShiftMetricsDTO).pendingRetrieval.toString()
                  }
                />
                <MetricCard
                  icon={Clock}
                  label={t('metrics.avgRetrieval')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : formatAvgSeconds((metrics as SupervisorShiftMetricsDTO).avgRetrievalSeconds)
                  }
                />
                <MetricCard
                  icon={UserCheck}
                  label={t('metrics.valetsRetrieving')}
                  value={
                    showMetricsSkeleton
                      ? null
                      : (metrics as SupervisorShiftMetricsDTO).activeValets.toString()
                  }
                />
              </div>
            )}
          </section>

          {/* ----- Active tickets list ----- */}
          <section>
            <p className="jq-eyebrow mb-4">{t('tickets.sectionTitle')}</p>

            {ticketsError ? (
              <ErrorState error={ticketsErrorObj} />
            ) : showTicketsSkeleton ? (
              <TicketListSkeleton />
            ) : items.length === 0 ? (
              <ListEmptyState />
            ) : (
              <div className="overflow-hidden rounded-sm border border-jq-sand/60 bg-jq-surface-0 shadow-sm">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="border-b border-jq-sand/60 bg-jq-surface-1/60">
                      <th className="px-4 py-3 text-left font-medium uppercase tracking-widest text-jq-graphite text-[11px]">
                        {t('tickets.columns.status')}
                      </th>
                      <th className="px-4 py-3 text-left font-medium uppercase tracking-widest text-jq-graphite text-[11px]">
                        {t('tickets.columns.plate')}
                      </th>
                      <th className="hidden px-4 py-3 text-left font-medium uppercase tracking-widest text-jq-graphite text-[11px] sm:table-cell">
                        {t('tickets.columns.zone')}
                      </th>
                      <th className="hidden px-4 py-3 text-left font-medium uppercase tracking-widest text-jq-graphite text-[11px] md:table-cell">
                        {t('tickets.columns.valet')}
                      </th>
                      <th className="px-4 py-3 text-right font-medium uppercase tracking-widest text-jq-graphite text-[11px]">
                        {t('tickets.columns.elapsed')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-jq-sand/40">
                    {items.map((ticket) => (
                      <TicketRow key={ticket.id} ticket={ticket} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// ── Ticket row ───────────────────────────────────────────────────────────────

function TicketRow({ ticket }: { ticket: SupervisorActiveTicketDTO }) {
  const { t } = useTranslation('supervisor-dashboard');
  const isPending = ticket.status === 'requested' && !ticket.valetEmail;
  const anchorIso = pickElapsedAnchor(ticket);
  const elapsed = useElapsed(anchorIso);

  return (
    <tr
      className={`transition-colors ${
        isPending ? 'bg-amber-50/60 hover:bg-amber-50' : 'hover:bg-jq-surface-1/40'
      }`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {isPending && (
            <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
          )}
          <StatusBadge status={ticket.status} isPending={isPending} />
        </div>
      </td>
      <td className="px-4 py-3 font-mono font-medium text-jq-navy">{ticket.plate}</td>
      <td className="hidden px-4 py-3 text-jq-graphite sm:table-cell">
        {ticket.parkingZone ?? '—'}
      </td>
      <td className="hidden px-4 py-3 text-jq-graphite md:table-cell">
        {ticket.valetEmail ?? (
          <span className="text-jq-stone italic">{t('tickets.unassigned')}</span>
        )}
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums text-jq-charcoal">{elapsed}</td>
    </tr>
  );
}

// ── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  isPending,
}: {
  status: SupervisorActiveTicketDTO['status'];
  isPending: boolean;
}) {
  const { t } = useTranslation('supervisor-dashboard');
  if (isPending) {
    return <Badge variant="warning">{t('status.pending')}</Badge>;
  }

  switch (status) {
    case 'parked':
      return <Badge variant="info">{t('status.parked')}</Badge>;
    case 'requested':
      return <Badge variant="warning">{t('status.requested')}</Badge>;
    case 'in_transit':
      return <Badge variant="success">{t('status.inTransit')}</Badge>;
  }
}

// ── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string | null;
}) {
  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm transition hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-jq-graphite">
          <Icon className="h-3.5 w-3.5 text-jq-gold-deep" strokeWidth={1.75} />
          {label}
        </div>
        {value === null ? (
          <Skeleton className="mt-2 h-8 w-16" />
        ) : (
          <p className="mt-2 font-editorial text-[28px] font-medium leading-none tracking-tight text-jq-navy tabular-nums">
            {value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Empty / error states ─────────────────────────────────────────────────────

function TrueEmptyState() {
  const { t } = useTranslation('supervisor-dashboard');
  return (
    <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-16 text-center">
      <Activity className="mx-auto mb-4 h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="font-editorial text-[18px] font-medium text-jq-navy">{t('emptyState.title')}</p>
      <p className="mt-2 text-[13px] text-jq-graphite">{t('emptyState.description')}</p>
    </div>
  );
}

function ListEmptyState() {
  const { t } = useTranslation('supervisor-dashboard');
  return (
    <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-10 text-center">
      <p className="text-[14px] text-jq-graphite">{t('listEmptyState.title')}</p>
      <p className="mt-1 text-[13px] text-jq-stone">{t('listEmptyState.description')}</p>
    </div>
  );
}

function NoLocationState() {
  const { t } = useTranslation('supervisor-dashboard');
  const { data, isLoading } = useQuery({
    queryKey: ['supervisor-my-locations'],
    queryFn: getMyLocations,
  });
  const activeOnes = data?.locations.filter((l) => l.status === 'active') ?? [];
  const onlyId = activeOnes.length === 1 ? activeOnes[0]?.id : undefined;

  // Exactly one assigned location: resolve it here (idempotent with AppShell's
  // auto-select) so the dashboard never depends on the topbar picker.
  useEffect(() => {
    if (onlyId) setActiveLocationId(onlyId);
  }, [onlyId]);

  if (isLoading || onlyId) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (data && activeOnes.length === 0) {
    return (
      <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-16 text-center">
        <p className="font-editorial text-[18px] font-medium text-jq-navy">
          {t('noLocation.noAssignedTitle')}
        </p>
        <p className="mt-2 text-[13px] text-jq-graphite">{t('noLocation.noAssignedDescription')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-jq-sand/60 bg-jq-surface-0 px-6 py-16 text-center">
      <p className="font-editorial text-[18px] font-medium text-jq-navy">{t('noLocation.title')}</p>
      <p className="mt-2 text-[13px] text-jq-graphite">{t('noLocation.description')}</p>
    </div>
  );
}

function ErrorState({ error }: { error: Error | null }) {
  const { t } = useTranslation('supervisor-dashboard');
  const message = translateApiError(error, t, { fallback: t('errorState.fallback') });
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-4 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="leading-snug">{message}</p>
    </div>
  );
}

function TicketListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function formatAvgSeconds(seconds: number): string {
  if (seconds === 0) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
